import {Box, IconButton, MenuItem, Stack, TextField, Tooltip, Typography} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const disabledBorderSx = {
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderColor: '#bdbdbd'
    }
};

const fieldLabelSx = {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--gray-darker, #222)'
};

const menuItemEllipsisSx = {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
};

const FieldLabel = ({ text, required, onReset, resetDisabled }) => (
    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 0.5, minHeight: 24 }}>
        <Typography sx={fieldLabelSx}>
            {text}{' '}
            <span style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>
                ({required ? '필수' : '선택'})
            </span>
        </Typography>
        {onReset && (
            <Tooltip title={resetDisabled ? '' : '초기화'}>
                <span>
                    <IconButton size='small' onClick={onReset} disabled={resetDisabled} sx={{ p: 0.25 }}>
                        <RestartAltIcon fontSize='small' />
                    </IconButton>
                </span>
            </Tooltip>
        )}
    </Stack>
);

const normalizeTree = (tree) => {
    if (!Array.isArray(tree)) return [];
    return tree
        .filter((node) => (node.depth ?? 1) === 1)
        .map((main) => ({
            id: main.id,
            name: main.categoryName || main.name,
            subs: Array.isArray(main.categoryList)
                ? main.categoryList.map((sub) => ({
                    id: sub.id,
                    name: sub.categoryName || sub.name
                }))
                : []
        }));
};

export default function CategorySelect({ mainId, subId, onChange, tree, size = 'small', required = true }) {
    const mains = normalizeTree(tree);
    const selectedMain = mains.find((m) => m.id === mainId);
    const subs = selectedMain?.subs || [];

    const handleMainChange = (e) => {
        onChange({ categoryMainId: e.target.value, categorySubId: null });
    };

    const handleSubChange = (e) => {
        onChange({ categorySubId: e.target.value });
    };

    const handleMainReset = () => {
        onChange({ categoryMainId: null, categorySubId: null });
    };

    const handleSubReset = () => {
        onChange({ categorySubId: null });
    };

    return (
        <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Stack direction='row' spacing={1.5} alignItems='flex-end'>
                <Box sx={{ flex: 1 }}>
                    <FieldLabel
                        text='1차 카테고리'
                        required={required}
                        onReset={handleMainReset}
                        resetDisabled={!mainId && !subId}
                    />
                    <TextField
                        select
                        size={size}
                        value={mainId ?? ''}
                        onChange={handleMainChange}
                        fullWidth
                        sx={{ '& .MuiSelect-select': { paddingTop: '16.5px', paddingBottom: '16.5px' } }}
                        slotProps={{
                            select: {
                                displayEmpty: true,
                                renderValue: (v) => {
                                    if (!v) return <span style={{ color: '#9e9e9e' }}>1차 카테고리를 선택해 주세요.</span>;
                                    return mains.find((m) => m.id === v)?.name ?? '';
                                }
                            }
                        }}
                    >
                        {mains.map((m) => (
                            <MenuItem key={m.id} value={m.id} sx={menuItemEllipsisSx}>{m.name}</MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <FieldLabel
                        text='2차 카테고리'
                        required={required}
                        onReset={handleSubReset}
                        resetDisabled={!subId}
                    />
                    <TextField
                        select
                        size={size}
                        value={subId ?? ''}
                        onChange={handleSubChange}
                        fullWidth
                        disabled={!selectedMain || subs.length === 0}
                        sx={{ ...disabledBorderSx, '& .MuiSelect-select': { paddingTop: '16.5px', paddingBottom: '16.5px' } }}
                        slotProps={{
                            select: {
                                displayEmpty: true,
                                renderValue: (v) => {
                                    if (!v) return <span style={{ color: '#9e9e9e' }}>2차 카테고리를 선택해 주세요.</span>;
                                    return subs.find((s) => s.id === v)?.name ?? '';
                                }
                            }
                        }}
                    >
                        {subs.map((s) => (
                            <MenuItem key={s.id} value={s.id} sx={menuItemEllipsisSx}>{s.name}</MenuItem>
                        ))}
                    </TextField>
                </Box>
            </Stack>
        </Stack>
    );
}

const ALL = '__ALL__';

const inlineLabelSx = {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--gray-darker, #222)',
    whiteSpace: 'nowrap'
};

export function CategoryFilter({ mainId, subId, onChange, tree }) {
    const mains = normalizeTree(tree);
    const selectedMain = mains.find((m) => m.id === mainId);
    const subs = selectedMain?.subs || [];

    const filterMenuProps = {
        PaperProps: {
            sx: { maxWidth: 160, width: 160 }
        }
    };

    return (
        <>
            <Stack direction='row' spacing={1} alignItems='center'>
                <Typography sx={inlineLabelSx}>1차 카테고리</Typography>
                <Box sx={{ width: 160, flexShrink: 0 }}>
                    <TextField
                        select
                        size='small'
                        fullWidth
                        value={mainId ?? ALL}
                        onChange={(e) => onChange({
                            categoryMainId: e.target.value === ALL ? null : e.target.value,
                            categorySubId: null
                        })}
                        sx={{ backgroundColor: '#fff' }}
                        SelectProps={{ MenuProps: filterMenuProps }}
                    >
                        <MenuItem value={ALL} sx={menuItemEllipsisSx}>전체</MenuItem>
                        {mains.map((m) => (
                            <MenuItem key={m.id} value={m.id} sx={menuItemEllipsisSx}>{m.name}</MenuItem>
                        ))}
                    </TextField>
                </Box>
            </Stack>
            <Stack direction='row' spacing={1} alignItems='center'>
                <Typography sx={inlineLabelSx}>2차 카테고리</Typography>
                <Box sx={{ width: 160, flexShrink: 0 }}>
                    <TextField
                        select
                        size='small'
                        fullWidth
                        value={subId ?? ALL}
                        onChange={(e) => onChange({
                            categorySubId: e.target.value === ALL ? null : e.target.value
                        })}
                        sx={{ ...disabledBorderSx, backgroundColor: '#fff' }}
                        disabled={!selectedMain}
                        SelectProps={{ MenuProps: filterMenuProps }}
                    >
                        <MenuItem value={ALL} sx={menuItemEllipsisSx}>전체</MenuItem>
                        {subs.map((s) => (
                            <MenuItem key={s.id} value={s.id} sx={menuItemEllipsisSx}>{s.name}</MenuItem>
                        ))}
                    </TextField>
                </Box>
            </Stack>
        </>
    );
}

export const findCategoryNames = (tree, mainId, subId) => {
    const mains = normalizeTree(tree);
    const main = mains.find((m) => m.id === mainId);
    if (!main) return { mainName: '', subName: '' };
    const sub = main.subs.find((s) => s.id === subId);
    return { mainName: main.name || '', subName: sub?.name || '' };
};

export const findCategoryNamesBySubId = (tree, subId) => {
    const mains = normalizeTree(tree);
    for (const main of mains) {
        const sub = main.subs.find((s) => s.id === subId);
        if (sub) return { mainId: main.id, mainName: main.name, subId: sub.id, subName: sub.name };
    }
    return { mainId: null, mainName: '', subId: null, subName: '' };
};
